/** @jsxImportSource @emotion/react */
// React/core
import React, { useState, useCallback, useRef } from 'react'
// Third-party
import { css, Global } from '@emotion/react'
import { Select, DatePicker, Input, Dropdown, Spin, App } from 'antd'
import type { MenuProps } from 'antd'
import {
  EditOutlined,
  MoreOutlined,
  LoadingOutlined,
  DownOutlined,
  CalendarOutlined,
  PlusOutlined,
} from '@ant-design/icons'
import type { Dayjs } from 'dayjs'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'

dayjs.extend(utc)
// Internal (@sportninja/common)
import colors from '@sportninja/common/constants/appColors'
// Relative
import { paragraphXSmall, weightSemiBold } from 'src/components/css'
import Icon from 'src/components/Icon'
import DeleteConfirmModal from './DeleteConfirmModal'
import { darkPortalStyles, selectAllItemStyles } from '../styles/shared'

const { RangePicker } = DatePicker

// =============================================================================
// Types
// =============================================================================

export interface BlackoutDateItem {
  id: string
  constraintId: string
  startDate: string
  endDate: string
  description: string
  locations: string[]
  type: string
  repeat: string
  divisions: string[]
}

export interface BlackoutDateFormData {
  dateRange: [Dayjs, Dayjs] | null
  description: string
  locations: string[]
  type: string
  repeat: string
  divisions: string[]
}

interface BlackoutDatesSectionProps {
  /** Blackout dates from the constraints hook */
  blackoutDates: BlackoutDateItem[]
  /** Available locations for dropdown */
  locationOptions?: { label: string; value: string }[]
  /** Available divisions for dropdown */
  divisionOptions?: { label: string; value: string }[]
  /** Loading state */
  loading?: boolean
  /** Handler for adding a blackout date */
  onAddBlackoutDate: (data: BlackoutDateFormData) => Promise<void>
  /** Handler for editing a blackout date */
  onEditBlackoutDate?: (id: string, data: BlackoutDateFormData) => Promise<void>
  /** Handler for deleting a blackout date */
  onDeleteBlackoutDate: (constraintId: string) => Promise<void>
}

// =============================================================================
// Styles
// =============================================================================

const containerStyles = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`

const headerRowStyles = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
`

const headerTitleContainerStyles = css`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
`

const titleStyles = css`
  ${weightSemiBold}
  color: ${colors.WHITE};
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  line-height: 16.8px;
  letter-spacing: 1.68px;
  text-transform: uppercase;
  margin: 0;
`

const descriptionStyles = css`
  ${paragraphXSmall}
  color: ${colors.NEUTRAL_50};
  margin: 0;
`

const headerButtonsStyles = css`
  display: flex;
  align-items: center;
  gap: 8px;
`

const outlineButtonStyles = css`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 8px;
  background: transparent;
  border: 1px solid ${colors.PRIMARY_300};
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${colors.PRIMARY_300}20;
  }
`

const outlineButtonTextStyles = css`
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20.3px;
  color: ${colors.PRIMARY_300};
`

const tableContainerStyles = css`
  background-color: ${colors.SECONDARY_400};
  border: 1px solid ${colors.SECONDARY_200};
  border-radius: 8px;
  overflow: hidden;
  width: 100%;
`

const tableHeaderStyles = css`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 4px 16px;
  background-color: ${colors.SECONDARY_300};
  width: 100%;
`

const tableRowStyles = css`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 8px 16px;
  width: 100%;

  &:hover {
    background-color: ${colors.SECONDARY_300}80;
  }
`

const inputRowStyles = css`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 8px 16px;
  width: 100%;
`

const columnHeaderStyles = css`
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 500;
  line-height: 17.4px;
  color: ${colors.NEUTRAL_70};
  text-transform: uppercase;
`

const columnCellStyles = css`
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20.3px;
  color: ${colors.NEUTRAL_30};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

// Column widths
const periodColumnStyles = css`
  width: 224px;
  flex-shrink: 0;
`

const flexColumnStyles = css`
  flex: 1;
  min-width: 0;
`

const repeatColumnStyles = css`
  width: 164px;
  flex-shrink: 0;
`

const divisionsColumnStyles = css`
  width: 164px;
  flex-shrink: 0;
`

const actionsColumnStyles = css`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  width: 120px;
  justify-content: flex-end;
`

const actionButtonStyles = css`
  background: transparent;
  border: none;
  color: ${colors.PRIMARY_300};
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;

  &:hover {
    color: ${colors.PRIMARY_200};
  }
`

const inputStyles = css`
  &.ant-input {
    background-color: ${colors.SECONDARY_200};
    border: 1px solid #3a4659;
    border-radius: 8px;
    height: 40px;
    padding: 8px 16px;
    font-family: 'Inter', sans-serif;
    font-size: 14px;
    font-weight: 500;
    color: ${colors.WHITE};

    &::placeholder {
      color: ${colors.NEUTRAL_100};
    }

    &:hover,
    &:focus {
      border-color: #3a4659;
      box-shadow: none;
    }

    &:focus {
      border-color: ${colors.PRIMARY_300};
    }
  }
`

const selectStyles = css`
  &.ant-select {
    min-height: 40px;

    .ant-select-selector {
      background-color: ${colors.SECONDARY_200} !important;
      border: 1px solid #3a4659 !important;
      border-radius: 8px !important;
      height: 40px !important;
      min-height: 40px !important;
      padding: 4px 8px !important;
      display: flex !important;
      align-items: center !important;
      flex-wrap: nowrap !important;
      overflow: hidden !important;
    }

    .ant-select-selection-item {
      font-family: 'Inter', sans-serif;
      font-size: 14px;
      font-weight: 500;
      color: ${colors.WHITE} !important;
      padding: 0 !important;
      padding-right: 20px !important;
    }

    .ant-select-selection-placeholder {
      font-family: 'Inter', sans-serif;
      font-size: 14px;
      font-weight: 500;
      color: ${colors.NEUTRAL_100} !important;
    }

    .ant-select-arrow {
      color: ${colors.WHITE} !important;
    }

    &:hover .ant-select-selector {
      border-color: #3a4659 !important;
    }

    &.ant-select-focused .ant-select-selector {
      border-color: ${colors.PRIMARY_300} !important;
      box-shadow: none !important;
    }

    /* Multi-select tag styles */
    .ant-select-selection-overflow-item .ant-select-selection-item {
      background-color: ${colors.SECONDARY_300} !important;
      border: 1px solid ${colors.SECONDARY_100} !important;
      border-radius: 4px !important;
      color: ${colors.WHITE} !important;
      font-size: 12px;
      padding: 0 4px !important;
      margin: 2px 4px 2px 0;
    }

    .ant-select-selection-item-remove {
      color: ${colors.NEUTRAL_100} !important;
      &:hover {
        color: ${colors.WHITE} !important;
      }
    }
  }
`

const datePickerStyles = css`
  &.ant-picker,
  &.ant-picker.ant-picker-outlined {
    background-color: ${colors.SECONDARY_200} !important;
    border: 1px solid #3a4659 !important;
    border-radius: 8px;
    height: 40px;
    padding: 0 16px;

    .ant-picker-input > input {
      font-family: 'Inter', sans-serif;
      font-size: 14px;
      font-weight: 500;
      color: ${colors.WHITE};

      &::placeholder {
        color: ${colors.NEUTRAL_100};
      }
    }

    .ant-picker-suffix {
      color: ${colors.WHITE};
      order: -1;
      margin-left: 0;
      margin-right: 8px;
    }

    .ant-picker-separator {
      color: ${colors.WHITE};
      padding: 0 4px;
    }

    .ant-picker-range-separator {
      padding: 0 4px;
    }

    .ant-picker-active-bar {
      display: none;
    }

    .ant-picker-clear {
      background: ${colors.SECONDARY_200};
      color: ${colors.WHITE};
    }

    &:hover {
      border-color: #3a4659;
    }

    &.ant-picker-focused {
      border-color: ${colors.PRIMARY_300};
      box-shadow: none;
    }
  }
`

const saveButtonStyles = css`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 16px 4px 8px;
  background: ${colors.PRIMARY_300};
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${colors.PRIMARY_200};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const saveButtonTextStyles = css`
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20.3px;
  color: ${colors.SECONDARY_300};
`

const loadingContainerStyles = css`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 24px;
`

const emptyRowStyles = css`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px 16px;
  color: ${colors.NEUTRAL_50};
  font-family: 'Inter', sans-serif;
  font-size: 14px;
`

// =============================================================================
// Component
// =============================================================================

const DEFAULT_REPEAT_OPTIONS = [
  { label: 'No Repeat', value: 'no_repeat' },
  { label: 'Weekly', value: 'weekly' },
  { label: 'Bi-weekly', value: 'bi_weekly' },
  { label: 'Monthly', value: 'monthly' },
]

export const BlackoutDatesSection: React.FC<BlackoutDatesSectionProps> = ({
  blackoutDates,
  locationOptions = [],
  divisionOptions = [],
  loading = false,
  onAddBlackoutDate,
  onEditBlackoutDate,
  onDeleteBlackoutDate,
}) => {
  const { message } = App.useApp()

  // Form state for the add row
  const [formData, setFormData] = useState<BlackoutDateFormData>({
    dateRange: null,
    description: '',
    locations: [],
    type: '',
    repeat: 'no_repeat',
    divisions: [],
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showAddRow, setShowAddRow] = useState(false)
  const [editingItemId, setEditingItemId] = useState<string | null>(null)

  // Refs to close dropdowns after "Select All"
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const locationsSelectRef = useRef<any>(null)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const divisionsSelectRef = useRef<any>(null)

  // Delete modal state
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [itemToDelete, setItemToDelete] = useState<{
    constraintId: string
    description: string
  } | null>(null)

  // Reset form
  const resetForm = useCallback(() => {
    setFormData({
      dateRange: null,
      description: '',
      locations: [],
      type: '',
      repeat: 'no_repeat',
      divisions: [],
    })
  }, [])

  // Handle add click
  const handleAddClick = useCallback(() => {
    setEditingItemId(null)
    resetForm()
    setShowAddRow(true)
  }, [resetForm])

  // Handle edit click — populate form with existing item data
  const handleEditClick = useCallback((item: BlackoutDateItem) => {
    const startDate = dayjs.utc(item.startDate)
    const endDate = dayjs.utc(item.endDate)
    setFormData({
      dateRange: [startDate, endDate],
      description: item.description || '',
      locations: item.locations || [],
      type: item.type || '',
      repeat: item.repeat || 'no_repeat',
      divisions: item.divisions || [],
    })
    setEditingItemId(item.constraintId)
    setShowAddRow(true)
  }, [])

  // Handle save
  const handleSave = useCallback(async () => {
    if (!formData.dateRange) {
      message.error('Please select a date range')
      return
    }

    if (formData.locations.length === 0) {
      message.error('Please select at least one location')
      return
    }

    if (formData.divisions.length === 0) {
      message.error('Please select at least one division')
      return
    }

    setIsSubmitting(true)
    try {
      if (editingItemId && onEditBlackoutDate) {
        await onEditBlackoutDate(editingItemId, formData)
        message.success('Blackout date updated successfully')
      } else {
        await onAddBlackoutDate(formData)
        message.success('Blackout date added successfully')
      }
      resetForm()
      setShowAddRow(false)
      setEditingItemId(null)
    } catch (error) {
      message.error(
        error instanceof Error ? error.message : 'Failed to save blackout date'
      )
    } finally {
      setIsSubmitting(false)
    }
  }, [
    formData,
    onAddBlackoutDate,
    onEditBlackoutDate,
    editingItemId,
    resetForm,
    message,
  ])

  // Handle delete click
  const handleDeleteClick = useCallback(
    (constraintId: string, description: string) => {
      setItemToDelete({ constraintId, description })
      setIsDeleteModalOpen(true)
    },
    []
  )

  // Handle confirm delete
  const handleConfirmDelete = useCallback(async () => {
    if (!itemToDelete) {
      return
    }
    await onDeleteBlackoutDate(itemToDelete.constraintId)
    setIsDeleteModalOpen(false)
    setItemToDelete(null)
  }, [itemToDelete, onDeleteBlackoutDate])

  // Handle close delete modal
  const handleCloseDeleteModal = useCallback(() => {
    setIsDeleteModalOpen(false)
    setItemToDelete(null)
  }, [])

  // Get row action menu items
  const getRowMenuItems = useCallback(
    (item: BlackoutDateItem): MenuProps['items'] => [
      {
        key: 'delete',
        label: 'Delete',
        danger: true,
        onClick: () => handleDeleteClick(item.constraintId, item.description),
      },
    ],
    [handleDeleteClick]
  )

  // Format period display
  const formatPeriod = useCallback((startDate: string, endDate: string) => {
    const start = dayjs(startDate)
    const end = dayjs(endDate)

    if (start.isSame(end, 'day')) {
      return start.format('MMM D, YYYY')
    }
    return `${start.format('MMM D')} - ${end.format('MMM D, YYYY')}`
  }, [])

  // Format locations display
  const formatLocations = useCallback(
    (locations: string[]) => {
      if (locations.length === 0) {
        return 'All Locations'
      }
      const labels = locations
        .map((loc) => locationOptions.find((o) => o.value === loc)?.label)
        .filter(Boolean)
      return labels.length > 0 ? labels.join(', ') : 'All Locations'
    },
    [locationOptions]
  )

  // Format divisions display
  const formatDivisions = useCallback(
    (divisions: string[]) => {
      if (divisions.length === 0) {
        return 'All Divisions'
      }
      const labels = divisions
        .map((div) => divisionOptions.find((o) => o.value === div)?.label)
        .filter(Boolean)
      return labels.length > 0 ? labels.join(', ') : 'All Divisions'
    },
    [divisionOptions]
  )

  // Format repeat display
  const formatRepeat = useCallback((repeat: string) => {
    const option = DEFAULT_REPEAT_OPTIONS.find((o) => o.value === repeat)
    return option?.label || 'No Repeat'
  }, [])

  return (
    <>
      <Global styles={darkPortalStyles} />
      <div css={containerStyles}>
        {/* Header Row */}
        <div css={headerRowStyles}>
          <div css={headerTitleContainerStyles}>
            <p css={titleStyles}>Blackout Dates</p>
            <p css={descriptionStyles}>
              Use this option to specify dates that will be blocked from
              schedule
            </p>
          </div>
          <div css={headerButtonsStyles}>
            <button
              type='button'
              css={outlineButtonStyles}
              onClick={handleAddClick}
            >
              <Icon name='plus' fontSize={24} color={colors.PRIMARY_300} />
              <span css={outlineButtonTextStyles}>Add</span>
            </button>
          </div>
        </div>

        {/* Table */}
        <div css={tableContainerStyles}>
          {/* Table Header */}
          <div css={tableHeaderStyles}>
            <div css={[columnHeaderStyles, periodColumnStyles]}>Period</div>
            <div css={[columnHeaderStyles, flexColumnStyles]}>Description</div>
            <div css={[columnHeaderStyles, flexColumnStyles]}>Locations</div>
            <div css={[columnHeaderStyles, repeatColumnStyles]}>Repeat</div>
            <div css={[columnHeaderStyles, divisionsColumnStyles]}>
              Divisions
            </div>
            <div css={actionsColumnStyles} style={{ opacity: 0 }}>
              <EditOutlined style={{ fontSize: 24 }} />
              <MoreOutlined style={{ fontSize: 24 }} />
            </div>
          </div>

          {/* Loading State */}
          {loading ? (
            <div css={loadingContainerStyles}>
              <Spin
                indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
              />
            </div>
          ) : (
            <>
              {/* Data Rows */}
              {blackoutDates.length === 0 && !showAddRow ? (
                <div css={emptyRowStyles}>
                  No blackout dates added yet. Click &quot;Add&quot; to add one.
                </div>
              ) : (
                blackoutDates.map((item) => (
                  <div key={item.id} css={tableRowStyles}>
                    <div css={[columnCellStyles, periodColumnStyles]}>
                      {formatPeriod(item.startDate, item.endDate)}
                    </div>
                    <div css={[columnCellStyles, flexColumnStyles]}>
                      {item.description || '-'}
                    </div>
                    <div css={[columnCellStyles, flexColumnStyles]}>
                      {formatLocations(item.locations)}
                    </div>
                    <div css={[columnCellStyles, repeatColumnStyles]}>
                      {formatRepeat(item.repeat)}
                    </div>
                    <div css={[columnCellStyles, divisionsColumnStyles]}>
                      {formatDivisions(item.divisions)}
                    </div>
                    <div css={actionsColumnStyles}>
                      <button
                        type='button'
                        css={actionButtonStyles}
                        onClick={() => handleEditClick(item)}
                        aria-label='Edit blackout date'
                      >
                        <EditOutlined style={{ fontSize: 24 }} />
                      </button>
                      <Dropdown
                        menu={{ items: getRowMenuItems(item) }}
                        trigger={['click']}
                        placement='bottomRight'
                      >
                        <button
                          type='button'
                          css={actionButtonStyles}
                          aria-label='More options'
                        >
                          <MoreOutlined style={{ fontSize: 24 }} />
                        </button>
                      </Dropdown>
                    </div>
                  </div>
                ))
              )}

              {/* Add Row (shown when adding) */}
              {showAddRow && (
                <div css={inputRowStyles}>
                  <div css={periodColumnStyles}>
                    <RangePicker
                      css={datePickerStyles}
                      value={formData.dateRange}
                      onChange={(dates) =>
                        setFormData((prev) => ({
                          ...prev,
                          dateRange: dates as [Dayjs, Dayjs] | null,
                        }))
                      }
                      disabledDate={(current) =>
                        current && current < dayjs().startOf('day')
                      }
                      format='MMM DD, YYYY'
                      placeholder={['Start date', 'End date']}
                      suffixIcon={
                        <CalendarOutlined style={{ color: colors.WHITE }} />
                      }
                      separator='-'
                      style={{ width: '100%' }}
                    />
                  </div>
                  <div css={flexColumnStyles}>
                    <Input
                      css={inputStyles}
                      value={formData.description}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          description: e.target.value,
                        }))
                      }
                      placeholder='Blackout day description'
                    />
                  </div>
                  <div css={flexColumnStyles}>
                    <Select
                      ref={locationsSelectRef}
                      mode='multiple'
                      css={selectStyles}
                      value={formData.locations}
                      onChange={(value) =>
                        setFormData((prev) => ({ ...prev, locations: value }))
                      }
                      options={locationOptions}
                      placeholder='Select locations'
                      maxTagCount='responsive'
                      allowClear
                      popupClassName='dark-select-popup'
                      dropdownRender={(menu) => (
                        <>
                          <div
                            css={selectAllItemStyles}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => {
                              const allValues = locationOptions.map(
                                (o) => o.value
                              )
                              const allSelected =
                                formData.locations.length === allValues.length
                              setFormData((prev) => ({
                                ...prev,
                                locations: allSelected ? [] : allValues,
                              }))
                              if (!allSelected) {
                                locationsSelectRef.current?.blur()
                              }
                            }}
                          >
                            All Locations
                          </div>
                          {menu}
                        </>
                      )}
                      style={{ width: '100%' }}
                    />
                  </div>
                  <div css={repeatColumnStyles}>
                    <Select
                      css={selectStyles}
                      value={formData.repeat}
                      onChange={(value) =>
                        setFormData((prev) => ({ ...prev, repeat: value }))
                      }
                      options={DEFAULT_REPEAT_OPTIONS}
                      suffixIcon={
                        <DownOutlined style={{ color: colors.WHITE }} />
                      }
                      style={{ width: '100%' }}
                    />
                  </div>
                  <div css={divisionsColumnStyles}>
                    <Select
                      ref={divisionsSelectRef}
                      mode='multiple'
                      css={selectStyles}
                      value={formData.divisions}
                      onChange={(value) =>
                        setFormData((prev) => ({ ...prev, divisions: value }))
                      }
                      options={divisionOptions}
                      placeholder='Select divisions'
                      maxTagCount='responsive'
                      allowClear
                      popupClassName='dark-select-popup'
                      dropdownRender={(menu) => (
                        <>
                          <div
                            css={selectAllItemStyles}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => {
                              const allValues = divisionOptions.map(
                                (o) => o.value
                              )
                              const allSelected =
                                formData.divisions.length === allValues.length
                              setFormData((prev) => ({
                                ...prev,
                                divisions: allSelected ? [] : allValues,
                              }))
                              if (!allSelected) {
                                divisionsSelectRef.current?.blur()
                              }
                            }}
                          >
                            All Divisions
                          </div>
                          {menu}
                        </>
                      )}
                      style={{ width: '100%' }}
                    />
                  </div>
                  <div css={actionsColumnStyles}>
                    <button
                      type='button'
                      css={saveButtonStyles}
                      onClick={handleSave}
                      disabled={isSubmitting}
                      aria-label='Save blackout date'
                    >
                      {isSubmitting ? (
                        <LoadingOutlined
                          style={{ fontSize: 18, color: colors.SECONDARY_300 }}
                        />
                      ) : (
                        <PlusOutlined
                          style={{ fontSize: 18, color: colors.SECONDARY_300 }}
                        />
                      )}
                      <span css={saveButtonTextStyles}>Save</span>
                    </button>
                    <Dropdown
                      menu={{
                        items: [
                          {
                            key: 'cancel',
                            label: 'Cancel',
                            onClick: () => {
                              resetForm()
                              setShowAddRow(false)
                              setEditingItemId(null)
                            },
                          },
                        ],
                      }}
                      trigger={['click']}
                      placement='bottomRight'
                    >
                      <button
                        type='button'
                        css={actionButtonStyles}
                        aria-label='More options'
                      >
                        <MoreOutlined style={{ fontSize: 24 }} />
                      </button>
                    </Dropdown>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Delete Confirmation Modal */}
        <DeleteConfirmModal
          isOpen={isDeleteModalOpen}
          onClose={handleCloseDeleteModal}
          onConfirm={handleConfirmDelete}
          title='Remove Blackout Date'
          message='Are you sure you want to remove this blackout date? This action cannot be undone.'
          itemName={itemToDelete?.description || 'Blackout Date'}
        />
      </div>
    </>
  )
}

export default BlackoutDatesSection
